import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useGSAPReveal } from "../util/useGSAPReveal";

interface BookProps {
  book: {
    title: string;
    subtitle: string;
    description: string;
    coverImage: string;
    link: string;
  };
}

const BookSection: React.FC<BookProps> = ({ book }) => {
  const [isMobileView, setIsMobileView] = useState(false);

  const headerRef = useGSAPReveal({ variant: "fade-up" });
  const bookRef = useGSAPReveal({ variant: "fade-up", delay: 0.15, duration: 1 });
  const contentRef = useGSAPReveal({ variant: "fade-up", delay: 0.25, duration: 1 });

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileView(window.innerWidth <= 900);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  return (
    <section
      style={{
        minHeight: "100vh",
        background: "transparent",
        padding: isMobileView ? "120px 20px 80px 20px" : "160px 80px 120px 80px",
        fontFamily: "'Inter', sans-serif",
        position: "relative",
        zIndex: 1,
        display: "flex",
        alignItems: "center",
      }}
    >
      {/* Decorative vertical line */}
      <div
        style={{
          position: "absolute",
          left: isMobileView ? "20px" : "80px",
          top: "10%",
          bottom: "10%",
          width: "1px",
          background: "linear-gradient(180deg, transparent, rgba(255,255,255,0.05) 20%, rgba(255,255,255,0.05) 80%, transparent)",
          display: isMobileView ? "none" : "block"
        }}
      />

      <div
        style={{
          maxWidth: "1400px",
          margin: "0 auto",
          position: "relative",
          width: "100%",
        }}
      >
        {/* Section Header */}
        <div
          ref={headerRef}
          style={{
            marginBottom: isMobileView ? "60px" : "100px",
            paddingLeft: isMobileView ? "0" : "60px",
            textAlign: isMobileView ? "center" : "left",
            display: "flex",
            flexDirection: isMobileView ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobileView ? "center" : "flex-end",
            gap: "24px"
          }}
        >
          <div>
            <div
              style={{
                width: '40px',
                height: '3px',
                background: '#C4956A',
                borderRadius: '2px',
                marginBottom: '24px',
                marginLeft: isMobileView ? 'auto' : '0',
                marginRight: isMobileView ? 'auto' : '0',
              }}
            />
            <h2
              style={{
                fontSize: isMobileView ? "40px" : "56px",
                fontWeight: 600,
                color: "#F5F5F5",
                fontFamily: "'Space Grotesk', sans-serif",
                marginBottom: "16px",
                letterSpacing: "-0.02em",
                lineHeight: 1.1,
              }}
            >
              Publications
            </h2>
            <p
              style={{
                fontSize: isMobileView ? "16px" : "20px",
                color: "#737373",
                fontWeight: 300,
                maxWidth: "600px",
                margin: isMobileView ? "0 auto" : "0",
              }}
            >
              Technical authorship and system design expertise
            </p>
          </div>

          <Link
            to="/publications"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "12px 22px",
              color: "#c8c8d0",
              textDecoration: "none",
              borderRadius: "999px",
              fontSize: "15px",
              fontWeight: 500,
              background: "rgba(255,255,255,0.02)",
              border: "1px solid rgba(196, 149, 106, 0.18)",
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = "#C4956A";
              e.currentTarget.style.background = "rgba(196, 149, 106, 0.07)";
              e.currentTarget.style.borderColor = "rgba(196, 149, 106, 0.28)";
              const arrow = e.currentTarget.querySelector('.explore-arrow') as HTMLElement;
              if (arrow) arrow.style.transform = "translate(3px, -3px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = "#c8c8d0";
              e.currentTarget.style.background = "rgba(255,255,255,0.02)";
              e.currentTarget.style.borderColor = "rgba(196, 149, 106, 0.18)";
              const arrow = e.currentTarget.querySelector('.explore-arrow') as HTMLElement;
              if (arrow) arrow.style.transform = "translateX(0)";
            }}
          >
            Explore Book Details
            <svg
              className="explore-arrow"
              width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              style={{ transition: "transform 0.3s ease" }}
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Link>
        </div>

        {/* Minimalist Split Layout */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobileView ? "1fr" : "1fr 1.2fr",
            gap: isMobileView ? "60px" : "100px",
            alignItems: "center",
            paddingLeft: isMobileView ? "0" : "60px",
          }}
        >
          {/* Left: Floating Book Cover */}
          <div
            ref={bookRef}
            style={{
              display: "flex",
              justifyContent: isMobileView ? "center" : "flex-start",
              perspective: "1000px"
            }}
          >
            <Link
              to="/publications"
              style={{
                position: "relative",
                display: "block",
                textDecoration: "none",
                cursor: "pointer",
                padding: isMobileView ? "0" : "10px 10px 0 0",
              }}
            >
              {!isMobileView && (
                <div
                  style={{
                    position: "absolute",
                    top: "0",
                    right: "0",
                    width: "76%",
                    height: "86%",
                    borderRadius: "24px",
                    border: "1px solid rgba(196, 149, 106, 0.12)",
                    background:
                      "linear-gradient(145deg, rgba(196, 149, 106, 0.035), rgba(255,255,255,0.01))",
                    pointerEvents: "none",
                  }}
                />
              )}

              <img
                src={book.coverImage}
                alt={book.title}
                loading="eager"
                decoding="async"
                style={{
                  width: isMobileView ? "280px" : "360px",
                  height: "auto",
                  aspectRatio: "3/4",
                  objectFit: "cover",
                  borderRadius: "4px 16px 16px 4px",
                  display: "block",
                  position: "relative",
                  zIndex: 1,
                  boxShadow: "-14px 26px 52px rgba(0,0,0,0.58)",
                  border: "1px solid rgba(255,255,255,0.05)",
                  borderLeft: "6px solid #2a2a2a", // spine effect
                  transition: "all 0.6s cubic-bezier(0.16, 1, 0.3, 1)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-8px) rotateY(-4deg) scale(1.015)";
                  e.currentTarget.style.boxShadow = "-22px 30px 60px rgba(0,0,0,0.72)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "none";
                  e.currentTarget.style.boxShadow = "-14px 24px 48px rgba(0,0,0,0.55)";
                }}
              />

              <div
                style={{
                  position: "absolute",
                  top: "10%",
                  left: "10%",
                  right: "10%",
                  bottom: "10%",
                  background: "rgba(196, 149, 106, 0.06)",
                  filter: "blur(40px)",
                  zIndex: -1,
                  opacity: 0.38,
                  transition: "opacity 0.6s ease",
                  pointerEvents: "none"
                }}
                className="book-glow"
              />
            </Link>
          </div>

          {/* Right: Content & Typography */}
          <div
            ref={contentRef}
            style={{
              textAlign: isMobileView ? "center" : "left",
            }}
          >
            {/* Ultra-minimal tags */}
            <div
              style={{
                display: "flex",
                gap: "12px",
                marginBottom: "20px",
                justifyContent: isMobileView ? "center" : "flex-start",
              }}
            >
              {["Technical Guide", "System Design"].map((tag, i) => (
                <span
                  key={i}
                  style={{
                    fontSize: "12px",
                    color: "#C4956A",
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    fontWeight: 500,
                    position: "relative",
                  }}
                >
                  {i > 0 && <span style={{ position: "absolute", left: "-8px", color: "rgba(255,255,255,0.2)" }}>/</span>}
                  {tag}
                </span>
              ))}
            </div>

            <h3
              style={{
                fontSize: isMobileView ? "32px" : "48px",
                fontWeight: 600,
                color: "#F5F5F5",
                fontFamily: "'Space Grotesk', sans-serif",
                marginBottom: "16px",
                lineHeight: "1.1",
                letterSpacing: "-0.02em",
              }}
            >
              {book.title}
            </h3>

            <p
              style={{
                fontSize: isMobileView ? "18px" : "22px",
                color: "#e4e4e7",
                opacity: 0.9,
                marginBottom: "32px",
                fontWeight: 400,
                fontStyle: "italic",
              }}
            >
              {book.subtitle}
            </p>

            {/* Minimalist Stats Row */}
            <div
              style={{
                display: "flex",
                gap: isMobileView ? "24px" : "0",
                marginBottom: "40px",
                justifyContent: isMobileView ? "center" : "flex-start",
                borderBottom: "1px solid rgba(255,255,255,0.05)",
                paddingBottom: "32px",
                flexWrap: "wrap",
              }}
            >
              {[
                { label: "Pages", value: "350+" },
                { label: "Rating", value: "4.8" },
                { label: "Year", value: "2024" },
              ].map((stat, i, items) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    paddingRight: isMobileView ? "0" : "32px",
                    marginRight: isMobileView || i === items.length - 1 ? "0" : "32px",
                    borderRight:
                      isMobileView || i === items.length - 1
                        ? "none"
                        : "1px solid rgba(255,255,255,0.06)",
                  }}
                >
                  <span style={{ color: "#C4956A", fontWeight: 500, fontSize: "24px", fontFamily: "'JetBrains Mono', monospace" }}>
                    {stat.value}
                  </span>
                  <span style={{ color: "#71717a", fontSize: "13px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    {stat.label}
                  </span>
                </div>
              ))}
            </div>

            <p
              style={{
                fontSize: "16px",
                lineHeight: "1.8",
                color: "#a1a1aa",
                marginBottom: "40px",
                fontWeight: 300,
                maxWidth: "500px",
                margin: isMobileView ? "0 auto 40px auto" : "0 0 40px 0",
              }}
            >
              {book.description}
            </p>

            <div
              style={{
                display: "flex",
                flexDirection: isMobileView ? "column" : "row",
                alignItems: "center",
                gap: "24px",
                justifyContent: isMobileView ? "center" : "flex-start",
              }}
            >
              {/* Sleek Action Button */}
              <a
                href={book.link}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "15px 30px",
                  background: "#C4956A",
                  color: "#0A0A0A",
                  textDecoration: "none",
                  borderRadius: "999px",
                  fontSize: "16px",
                  fontWeight: 600,
                  transition: "all 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
                  border: "1px solid rgba(196, 149, 106, 0.2)",
                  boxShadow: "0 10px 24px rgba(196, 149, 106, 0.12)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 14px 30px rgba(196, 149, 106, 0.2)";
                  const arrow = e.currentTarget.querySelector('.arrow-icon') as HTMLElement;
                  if (arrow) arrow.style.transform = "translate(3px, -3px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 10px 24px rgba(196, 149, 106, 0.12)";
                  const arrow = e.currentTarget.querySelector('.arrow-icon') as HTMLElement;
                  if (arrow) arrow.style.transform = "translateX(0)";
                }}
              >
                <span>Read on Amazon</span>
                <svg
                  className="arrow-icon"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ transition: "transform 0.3s ease" }}
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </a>

              {/* Minimal Co-author */}
              <div style={{ display: "flex", alignItems: "center", gap: "10px", color: "#71717a", fontSize: "14px" }}>
                <span>with</span>
                <a
                  href="https://huzaifa.io"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#ffffff",
                    textDecoration: "none",
                    fontWeight: 500,
                    position: "relative",
                    display: "inline-block",
                  }}
                  onMouseEnter={(e) => {
                    const underline = e.currentTarget.querySelector('.underline') as HTMLElement;
                    if (underline) underline.style.transform = "scaleX(1)";
                  }}
                  onMouseLeave={(e) => {
                    const underline = e.currentTarget.querySelector('.underline') as HTMLElement;
                    if (underline) underline.style.transform = "scaleX(0)";
                  }}
                >
                  Huzaifa Asif
                  <span
                    className="underline"
                    style={{
                      position: "absolute",
                      bottom: "-2px",
                      left: 0,
                      width: "100%",
                      height: "1px",
                      background: "rgba(255,255,255,0.5)",
                      transform: "scaleX(0)",
                      transformOrigin: "right",
                      transition: "transform 0.3s ease",
                    }}
                  />
                </a>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
};

export default BookSection;
